import React, { useState, useEffect } from 'react';
import { auth, db, googleProvider } from './firebase';
import {
  signInWithPopup,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';

import { useQlikData } from './hooks/useQlikData';
import { getVader } from './qlik';
import { logActivity } from './activity';

import HotellBlock      from './blocks/HotellBlock';
import BrasserieBlock   from './blocks/BrasserieBlock';
import BelaggningBlock  from './blocks/BelaggningBlock';
import KnallenBlock     from './blocks/KnallenBlock';
import SportcenterBlock from './blocks/SportcenterBlock';
import LedningBlock     from './blocks/LedningBlock';
import StugserviceBlock from './blocks/StugserviceBlock';
import LanthandelBlock  from './blocks/LanthandelBlock';
import BudgetBlock      from './blocks/BudgetBlock';
import JournalBlock     from './blocks/JournalBlock';
import AdminBlock       from './blocks/AdminBlock';
import FeedbackButton   from './components/FeedbackButton';
import CardActions      from './components/CardActions';
import Birger           from './components/Birger';

import './App.css';

// ── VÄDERCHIP (Netatmo, gårdens station) ──────────────────────────
// Liten temperatur i nav-raden; klick fäller ut detaljkort. Renderar
// inget alls tills väderdata finns i cachen (secrets satta + prefetch).
function VaderChip() {
  const [vader, setVader] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let live = true;
    const hamta = () => getVader().then((v) => { if (live) setVader(v); }).catch(() => {});
    hamta();
    const iv = setInterval(hamta, 5 * 60 * 1000);
    return () => { live = false; clearInterval(iv); };
  }, []);

  const nu = vader?.nu;
  if (!nu?.ute || typeof nu.ute.temp !== 'number') return null;

  const t = nu.ute.temp;
  const ikon = (nu.regn?.timme > 0) ? (t <= 1 ? '🌨' : '🌧') : t <= 0 ? '❄' : '🌡';
  const fmtT = (v) => `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(Math.round(v * 10) / 10).toLocaleString('sv-SE')}°`;
  const gammal = Date.now() - (nu.tid || 0) > 90 * 60 * 1000; // >1,5 h = stationen tyst

  const rad = (label, varde) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: 12.5, padding: '2px 0' }}>
      <span style={{ color: 'var(--text-secondary)' }}>{label}</span><span style={{ fontWeight: 600 }}>{varde}</span>
    </div>
  );

  return (
    <span style={{ position: 'relative', display: 'inline-flex' }} data-noprint="1">
      <button onClick={() => setOpen((o) => !o)}
        title={`Gårdens väderstation${gammal ? ' — ingen färsk data' : ''}`}
        className="vader-chip"
        style={{ opacity: gammal ? 0.5 : 1 }}>
        {ikon} {fmtT(t)}
      </button>
      {open && (
        <div style={{ position: 'absolute', top: '110%', right: 0, zIndex: 900, background: 'var(--card)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 10, boxShadow: '0 6px 24px rgba(0,0,0,0.25)', padding: '10px 14px', minWidth: 210, fontFamily: 'system-ui,sans-serif' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-secondary)' }}>{(nu.station || '').replace(/\s*\((inne|indoor)\)\s*/gi, ' ').trim()}</span>
            <button onClick={() => setOpen(false)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1 }}>✕</button>
          </div>
          {rad('Ute', `${fmtT(nu.ute.temp)} ${nu.ute.trend === 'up' ? '↗' : nu.ute.trend === 'down' ? '↘' : ''}`)}
          {typeof nu.ute.min === 'number' && rad('Dygn', `${fmtT(nu.ute.min)} … ${fmtT(nu.ute.max)}`)}
          {typeof nu.ute.fukt === 'number' && rad('Luftfuktighet', `${nu.ute.fukt} %`)}
          {nu.vind && rad('Vind', `${Math.round(nu.vind.styrka)} (${Math.round(nu.vind.byar)}) m/s`)}
          {nu.regn && rad('Regn idag', `${(nu.regn.dygn ?? 0).toLocaleString('sv-SE')} mm`)}
          {/* Innetemp visas inte — basstationen står i ett förråd/trapphus */}
          <div style={{ fontSize: 10.5, color: 'var(--text-secondary)', marginTop: 6 }}>
            {gammal ? '⚠ Ingen färsk data från stationen' : `Uppdaterad ${new Date(nu.tid).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}`}
          </div>
        </div>
      )}
    </span>
  );
}

// ── AVATAR-MENY ───────────────────────────────────────────────────
// Rund ring med inloggade mailens första bokstav; klick → Logga ut.
// Ersätter "Logga ut"-knappen (sparar plats, särskilt på mobil).
function AvatarMeny({ email }) {
  const [open, setOpen] = useState(false);
  const bokstav = (email || '?').charAt(0).toUpperCase();
  return (
    <span style={{ position: 'relative', display: 'inline-flex' }} data-noprint="1">
      <button onClick={() => setOpen((o) => !o)} className="avatar-btn"
        title={email || ''} aria-label="Kontomeny">
        {bokstav}
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '120%', right: 0, zIndex: 950,
          background: 'var(--card)', color: 'var(--text)', border: '1px solid var(--border)',
          borderRadius: 10, boxShadow: '0 6px 24px rgba(0,0,0,0.25)', padding: 8,
          minWidth: 180, fontFamily: 'system-ui,sans-serif',
        }}>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', padding: '2px 8px 8px', borderBottom: '1px solid var(--border)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {email}
          </div>
          <button onClick={() => signOut(auth)}
            style={{ display: 'block', width: '100%', textAlign: 'left', background: 'transparent', border: 'none', padding: '8px 8px 4px', fontSize: 12.5, cursor: 'pointer', color: 'var(--text)', fontFamily: 'system-ui,sans-serif' }}>
            Logga ut
          </button>
        </div>
      )}
    </span>
  );
}

// ── NATTLÄGE ──────────────────────────────────────────────────────
// v1: global invertering via CSS-filter (html[data-theme='dark'] i App.css).
// Persisteras per webbläsare. Knapp nere till höger bredvid Feedback.
function ThemeToggle() {
  const [dark, setDark] = useState(() => {
    try { return localStorage.getItem('intel_theme') === 'dark'; } catch (_) { return false; }
  });
  useEffect(() => {
    document.documentElement.dataset.theme = dark ? 'dark' : 'light';
    try { localStorage.setItem('intel_theme', dark ? 'dark' : 'light'); } catch (_) {}
  }, [dark]);
  return (
    <button
      onClick={() => setDark(d => !d)}
      data-noprint="1"
      title={dark ? 'Dagläge' : 'Nattläge'}
      aria-label={dark ? 'Byt till dagläge' : 'Byt till nattläge'}
      style={{
        position: 'fixed', bottom: 28, right: 168, zIndex: 900,
        width: 36, height: 36, borderRadius: '50%',
        background: '#1B2A4A', color: '#fff', border: 'none',
        fontSize: 15, cursor: 'pointer', boxShadow: '0 2px 12px rgba(0,0,0,0.24)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      {dark ? '☀️' : '🌙'}
    </button>
  );
}

const TABS = [
  { id: 'hotell',      label: 'Hotell' },
  { id: 'belaggning',  label: 'Beläggning' },
  { id: 'brasserie',   label: 'Brasserie' },
  { id: 'knallen',     label: 'Knallen' },
  { id: 'sportcenter', label: 'Sportcenter' },
  { id: 'stugservice', label: 'Stugservice' },
  { id: 'lanthandel',  label: 'Lanthandeln' },
  { id: 'ledning',     label: 'Ledning' },
  { id: 'budget',      label: 'Budget' },
  { id: 'journal',     label: 'Journal' },
  { id: 'admin',       label: 'Admin' },
];

// Journalen är personlig — hårdlåst till ägarens e-post (även i firestore.rules)
const JOURNAL_AGARE = 'magnus@bruksvallsliden.se';

function roleToBlocks(role) {
  const map = {
    ledning:     ['hotell', 'belaggning', 'brasserie', 'knallen', 'sportcenter', 'stugservice', 'ledning'],
    hotell:      ['hotell', 'belaggning'],
    brasserie:   ['brasserie'],
    knallen:     ['knallen'],
    sportcenter: ['sportcenter'],
    stugservice: ['stugservice'],
  };
  return map[role] || [];
}

// Får användaren se en flik? Admin ser allt. Beläggning följer Hotell-åtkomst
// (så befintlig hotellpersonal får den utan att blocks-arrayen migreras).
function canSeeTab(tabId, blocks, isAdmin, email) {
  if (tabId === 'admin') return isAdmin;
  // Budget är delbar: admin ger blocket i Admin-fliken. Icke-admin får se och
  // redigera matrisen, men generatorn, fast/säsong-panelen och Bokförings-
  // fliken förblir admin-låsta — de läser hela resultaträkningen och
  // individlöner. Se firestore.rules för samma gräns på datanivå.
  if (tabId === 'journal') return email === JOURNAL_AGARE; // personlig — endast ägaren
  if (isAdmin) return true;
  if ((blocks || []).includes(tabId)) return true;
  if (tabId === 'belaggning' && (blocks || []).includes('hotell')) return true;
  return false;
}

// ── LOGIN SCREEN ──────────────────────────────────────────────────
function LoginScreen() {
  const [showExternal, setShowExternal] = useState(false);
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [googleErr, setGoogleErr] = useState(
    () => { const e = sessionStorage.getItem('loginError'); sessionStorage.removeItem('loginError'); return e || ''; }
  );
  const [pwErr, setPwErr]   = useState('');
  const [loading, setLoading] = useState(false);

  const handleGoogle = async () => {
    setLoading(true); setGoogleErr('');
    try {
      const result = await signInWithPopup(auth, googleProvider);
      if (!result.user.email.endsWith('@bruksvallsliden.se')) {
        sessionStorage.setItem('loginError', 'Endast @bruksvallsliden.se-konton är tillåtna via Google.');
        await signOut(auth);
      } else {
        logActivity('login');
      }
    } catch (e) {
      if (e.code !== 'auth/popup-closed-by-user') setGoogleErr('Inloggning misslyckades. Försök igen.');
    } finally { setLoading(false); }
  };

  const handlePassword = async () => {
    setLoading(true); setPwErr('');
    try {
      await signInWithEmailAndPassword(auth, email, password);
      logActivity('login');
    } catch {
      setPwErr('Felaktig e-post eller lösenord.');
    } finally { setLoading(false); }
  };

  return (
    <div className="login-wrap">
      <div className="login-card">
        <div className="login-logo">
          <img src="/logo.png" alt="Bruksvallsliden" style={{ height: 140, width: 'auto', maxWidth: '100%' }} />
        </div>
        <h1 className="login-title">Intel</h1>
        <p className="login-sub">Intern affärsintelligens</p>

        <button className="login-google-btn" onClick={handleGoogle} disabled={loading}>
          <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
            <path d="M17.64 9.2a10.34 10.34 0 0 0-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.91a8.78 8.78 0 0 0 2.69-6.62Z" fill="#4285F4"/>
            <path d="M9 18a8.59 8.59 0 0 0 5.96-2.18l-2.91-2.26a5.4 5.4 0 0 1-8.07-2.84H.96v2.33A9 9 0 0 0 9 18Z" fill="#34A853"/>
            <path d="M3.96 10.71A5.41 5.41 0 0 1 3.68 9c0-.59.1-1.17.28-1.71V4.96H.96A9 9 0 0 0 0 9c0 1.45.35 2.82.96 4.04l3-2.33Z" fill="#FBBC05"/>
            <path d="M9 3.58a4.86 4.86 0 0 1 3.44 1.35l2.58-2.58A8.64 8.64 0 0 0 9 0 9 9 0 0 0 .96 4.96l3 2.33A5.36 5.36 0 0 1 9 3.58Z" fill="#EA4335"/>
          </svg>
          Logga in med Google
        </button>

        {googleErr && <div className="login-error" style={{ marginTop: 8 }}>{googleErr}</div>}

        <div className="login-divider"><span>extern åtkomst</span></div>

        <button className="login-external-toggle" onClick={() => setShowExternal(v => !v)}>
          {showExternal ? '▲ Dölj' : '▼ E-post & lösenord'}
        </button>

        {showExternal && (
          <div className="login-external">
            <input
              className="login-input"
              type="email"
              placeholder="E-postadress"
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
            <input
              className="login-input"
              type="password"
              placeholder="Lösenord"
              value={password}
              onChange={e => setPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handlePassword()}
            />
            {pwErr && <div className="login-error">{pwErr}</div>}
            <button
              className="login-btn"
              onClick={handlePassword}
              disabled={loading || !email || !password}
            >
              {loading ? 'Loggar in...' : 'Logga in'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── MAIN APP ──────────────────────────────────────────────────────
export default function App() {
  const [user, setUser]         = useState(null);
  const [userPerms, setUserPerms] = useState(null); // { blocks: string[], isAdmin: bool }
  const [authLoading, setAuthLoading] = useState(true);
  const [activeTab, setActiveTab]     = useState(null);
  const [menuOpen, setMenuOpen]       = useState(false);
  const { data, loading, error, lastUpdated, refresh } = useQlikData();

  useEffect(() => {
    return onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        try {
          const ref = doc(db, 'intel_users', u.email);
          let snap = await getDoc(ref);
          const provider = u.providerData?.[0]?.providerId === 'google.com' ? 'google' : 'password';

          if (!snap.exists()) {
            await setDoc(ref, {
              email: u.email,
              displayName: u.displayName || u.email,
              provider,
              isAdmin: false,
              blocks: [],
              createdAt: serverTimestamp(),
            });
            snap = await getDoc(ref);
          } else if (!snap.data().email) {
            // Självläkning: partiellt skapad post (t.ex. avbruten första
            // inloggning) — fyll på basfälten utan att röra behörigheter
            await setDoc(ref, {
              email: u.email,
              displayName: u.displayName || u.email,
              provider,
            }, { merge: true });
            snap = await getDoc(ref);
          }

          const d = snap.data();
          const blocks  = d.blocks  ?? roleToBlocks(d.role);
          const isAdmin = d.isAdmin || false;
          setUserPerms({ blocks, isAdmin });

          const visible = TABS.filter(t => canSeeTab(t.id, blocks, isAdmin, u.email));
          // Landa på senast besökta flik (per användare, lokalt i webbläsaren) —
          // men bara om behörigheten fortfarande täcker den. Annars första synliga.
          let lastTab = null;
          try { lastTab = localStorage.getItem('intel_last_tab_' + u.email); } catch (_) {}
          setActiveTab(visible.some(t => t.id === lastTab) ? lastTab : (visible[0]?.id || null));
        } catch (e) {
          console.error(e);
          setUserPerms({ blocks: [], isAdmin: false });
        }
      } else {
        setUserPerms(null);
      }
      setAuthLoading(false);
    });
  }, []);

  // Logga sidvisning (aggregerat) när aktiv flik ändras + kom ihåg den som startsida
  useEffect(() => {
    if (activeTab) {
      logActivity('view', activeTab);
      try { if (user?.email) localStorage.setItem('intel_last_tab_' + user.email, activeTab); } catch (_) {}
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  if (authLoading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!user)       return <LoginScreen />;

  const hasAccess = userPerms?.isAdmin || (userPerms?.blocks?.length > 0);
  if (!hasAccess) return (
    <div className="login-wrap">
      <div className="login-card">
        <h1 className="login-title">Ingen åtkomst</h1>
        <p className="login-sub">Du har inte tillgång till portalen ännu.<br />Kontakta Magnus för att få behörighet.</p>
        <button className="login-btn" style={{ marginTop: '1.5rem' }} onClick={() => signOut(auth)}>
          Logga ut
        </button>
      </div>
    </div>
  );

  const visibleTabs = TABS.filter(t => canSeeTab(t.id, userPerms?.blocks, userPerms.isAdmin, user?.email));

  return (
    <div className="app" onClick={() => menuOpen && setMenuOpen(false)}>
      <nav className="nav">
        <div className="nav-brand">
          <img src="/logo.png" alt="Bruksvallsliden" className="nav-logo" />
          <span className="nav-brand-text">Intel</span>
        </div>
        <div className="nav-tabs">
          {visibleTabs.map(t => (
            <button
              key={t.id}
              className={'nav-tab ' + (activeTab === t.id ? 'active' : '')}
              onClick={() => setActiveTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
        <button
          className="nav-hamburger"
          onClick={e => { e.stopPropagation(); setMenuOpen(v => !v); }}
          aria-label="Meny"
        >
          <span /><span /><span />
        </button>
        <div className="nav-right">
          <VaderChip />
          {lastUpdated && (
            <span className="last-updated">
              Uppdaterad {lastUpdated.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <button
            className="refresh-btn"
            onClick={() => refresh(userPerms.isAdmin ? 'force' : 'reread')}
            disabled={loading}
            title={userPerms.isAdmin ? 'Hämta färsk data från Qlik' : 'Uppdatera'}
          >↻</button>
          <button className="logout-btn" data-noprint="1" onClick={() => window.print()}
            title="Spara sidan som PDF eller skriv ut">⤓ PDF</button>
          <AvatarMeny email={user?.email} />
        </div>
      </nav>
      {menuOpen && (
        <div className="nav-mobile-menu" onClick={e => e.stopPropagation()}>
          {visibleTabs.map(t => (
            <button
              key={t.id}
              className={'nav-mobile-item' + (activeTab === t.id ? ' active' : '')}
              onClick={() => { setActiveTab(t.id); setMenuOpen(false); }}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {loading && <div className="data-loading">Hämtar data från Qlik...</div>}
      {error   && <div className="data-error">Fel vid datahämtning: {error}</div>}

      <main className="main">
        {/* Print-only varumärkesrad (loggan döljs annars i utskrift) */}
        <div className="print-brand">
          <img src="/logo.png" alt="Bruksvallsliden" />
          <span>Bruksvallsliden · Intel</span>
          <span className="print-brand-date">{new Date().toLocaleDateString('sv-SE')}</span>
        </div>
        {activeTab === 'hotell'      && <HotellBlock      data={data} />}
        {activeTab === 'belaggning'  && <BelaggningBlock />}
        {activeTab === 'brasserie'   && <BrasserieBlock   data={data} isAdmin={userPerms.isAdmin} />}
        {activeTab === 'knallen'     && <KnallenBlock     data={data} />}
        {activeTab === 'sportcenter' && <SportcenterBlock data={data} isAdmin={userPerms.isAdmin} />}
        {activeTab === 'stugservice' && <StugserviceBlock data={data} />}
        {activeTab === 'lanthandel'  && <LanthandelBlock />}
        {activeTab === 'ledning'     && <LedningBlock     data={data} isAdmin={userPerms.isAdmin} />}
        {activeTab === 'budget'      && <BudgetBlock isAdmin={userPerms.isAdmin} />}
        {activeTab === 'journal'     && user?.email === JOURNAL_AGARE && <JournalBlock />}
        {activeTab === 'admin'       && <AdminBlock currentUserEmail={user?.email} />}
      </main>
      <CardActions />
      <ThemeToggle />
      <FeedbackButton />
      <Birger nivaFull={userPerms.isAdmin || (userPerms?.blocks || []).includes('ledning')} />
    </div>
  );
}
